import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Heading from "@/components/atoms/Heading";
import Text from "@/components/atoms/Text";
import { useInspectionFormat } from "../useInspectionFormat";
import { DeviationPreview } from "../useDeviationPreview";
import styles from "../styles.module.css";

interface CostSummaryProps {
  preview: DeviationPreview;
}

/**
 * What the deviations cost the supplier, as the backend's preview priced them: the value adjustment for the
 * deviation products with the order's weight split, the services, and the total. "—" until they're priced.
 */
export default function CostSummary({ preview }: CostSummaryProps) {
  const { t } = useTranslation();
  const format = useInspectionFormat();
  const { costs, error } = preview;
  const amount = (value: number | undefined) =>
    costs && value !== undefined ? format.amount(value, costs.currency) : "—";

  return (
    <div className={styles.fieldStack} data-testid="inspection-cost-summary">
      <Heading order={3} styleOrder={6} strong>
        {t("inspections:costs.title")}
      </Heading>
      <div className={styles.costList}>
        <CostRow
          label={t("inspections:costs.productsValueAdjustment")}
          detail={
            costs && costs.deductedWeightKg > 0
              ? t("inspections:costs.split", {
                  contractWeight: format.number(costs.contractWeightKg),
                  deductedWeight: format.number(costs.deductedWeightKg),
                })
              : null
          }
          amount={amount(costs?.productsTotal)}
          testId="inspection-products-cost"
        />
        <CostRow
          label={t("inspections:deviations.servicesTitle")}
          amount={amount(costs?.servicesTotal)}
          testId="inspection-services-cost"
        />
        <div className={`${styles.costRow} ${styles.costTotal}`}>
          <Text display="technical" size="xs" strong tt="uppercase">
            {t("inspections:costs.total")}
          </Text>
          <Text size="md" strong data-testid="inspection-total-deviation-amount">
            {amount(costs?.total)}
          </Text>
        </div>
      </div>
      {error ? (
        <Text size="xs" color="error" data-testid="inspection-cost-error">
          {error}
        </Text>
      ) : (
        preview.isIncomplete && (
          <Text size="xs" color="tertiary" data-testid="inspection-cost-incomplete">
            {t("inspections:costs.incomplete")}
          </Text>
        )
      )}
    </div>
  );
}

interface CostRowProps {
  label: string;
  detail?: ReactNode;
  amount: string;
  testId: string;
}

const CostRow = ({ label, detail, amount, testId }: CostRowProps) => (
  <div className={styles.costRow}>
    <div>
      <Text size="sm">{label}</Text>
      {detail && (
        <Text size="xs" color="secondary" data-testid={`${testId}-detail`}>
          {detail}
        </Text>
      )}
    </div>
    <Text size="sm" className={styles.alignRight} data-testid={testId}>
      {amount}
    </Text>
  </div>
);
